/* Spectral analysis, in the browser.
 *
 * Why here and not on the server: the receiver has never opened an uploaded
 * object's bytes, there is no worker, no `levels_1min` table and no curated
 * zone. A server-side spectrogram would mean building all of that first. But
 * the browser already has the decoded audio -- it fetched it straight from
 * object storage with a presigned URL, because the receiver never carries
 * media (R7.2) -- so the transform is free, costs the VM nothing, and scales
 * with the number of people looking rather than the size of the fleet.
 *
 * What it is NOT: calibrated. Everything here is dBFS, relative to digital
 * full scale. Converting to dB re 1 µPa needs all three R2.3 terms, the server
 * decides whether they are known (app/calibration.py), and the UI prints that
 * verdict rather than computing one. A spectrogram with no absolute reference
 * is still the most useful view of a hydrophone there is -- you can see a
 * vessel, a pile driver, rain, a snapping shrimp bed -- and mislabelling its
 * axis would be the one way to make it worthless.
 */

/* -------------------------------------------------------------------- FFT */

/** In-place iterative radix-2 Cooley-Tukey. `re`/`im` must be a power of two. */
export function fft(re: Float32Array, im: Float32Array): void {
  const n = re.length

  // Bit-reversal permutation.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1
    for (; j & bit; bit >>= 1) j ^= bit
    j ^= bit
    if (i < j) {
      let t = re[i]!; re[i] = re[j]!; re[j] = t
      t = im[i]!; im[i] = im[j]!; im[j] = t
    }
  }

  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len
    const wRe = Math.cos(ang)
    const wIm = Math.sin(ang)
    for (let i = 0; i < n; i += len) {
      let curRe = 1
      let curIm = 0
      const half = len >> 1
      for (let k = 0; k < half; k++) {
        const aRe = re[i + k]!, aIm = im[i + k]!
        const bRe = re[i + k + half]!, bIm = im[i + k + half]!
        const tRe = bRe * curRe - bIm * curIm
        const tIm = bRe * curIm + bIm * curRe
        re[i + k] = aRe + tRe
        im[i + k] = aIm + tIm
        re[i + k + half] = aRe - tRe
        im[i + k + half] = aIm - tIm
        const nextRe = curRe * wRe - curIm * wIm
        curIm = curRe * wIm + curIm * wRe
        curRe = nextRe
      }
    }
  }
}

/** Periodic Hann. Sidelobes matter here: a ship's tonal at -60 dBFS sitting
 *  next to a loud low-frequency band is exactly what a rectangular window
 *  would smear away. */
export function hann(n: number): Float32Array {
  const w = new Float32Array(n)
  for (let i = 0; i < n; i++) w[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / n))
  return w
}

/** What one decoded clip actually measures, before anything is done to it. */
export interface Levels {
  /** Loudest single sample, in dBFS. Transients set this. */
  peakDbfs: number
  /** Root mean square over the whole clip, in dBFS. Loudness follows this. */
  rmsDbfs: number
  /** A sample reached or exceeded full scale, so headroom was already gone
   *  before this code saw it and normalising cannot undo the damage. */
  clipped: boolean
  /** True when there is no signal at all -- every sample exactly zero. Kept
   *  separate from a very low rmsDbfs, because they need different words: one
   *  is a quiet recording, the other is not a recording. */
  silent: boolean
}

/** The floor reported instead of -Infinity for digital silence.
 *
 *  -Infinity is the honest value and an impossible one to render or arithmetic
 *  with: it formats as "-Infinity dBFS" and turns any normalisation gain into
 *  Infinity. Callers get a finite number and the `silent` flag, so they can say
 *  "no signal" rather than printing a number that looks measured. -200 dBFS is
 *  far below the -144 dBFS noise floor of 24-bit audio, so it can never be
 *  confused with a real measurement. */
export const SILENT_DBFS = -200

/** Peak and RMS of one decoded channel, in dBFS relative to 1.0 full scale.
 *
 *  **dBFS, not dB re 1 µPa.** This is a property of the numbers in the buffer,
 *  not of the sea. Turning it into an absolute sound pressure needs the
 *  hydrophone's sensitivity and the whole gain chain, which live on the server
 *  as `calibration`; until a mission is calibrated, rule 5 says these figures
 *  are relative and may not be compared between sites.
 *
 *  One pass, no allocation. Cheap enough to run on every clip that is opened:
 *  the samples are already decoded and in memory by the time this is called,
 *  and the FFT that follows costs orders of magnitude more.
 */
export function measureLevels(samples: Float32Array): Levels {
  let peak = 0
  // Summed as float64 -- the accumulator is a JS number regardless of the
  // Float32Array it reads from. An hour of audio is ~170M samples and a float32
  // accumulator would lose the quiet ones entirely once the sum grew large.
  let sumSquares = 0
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i]!
    const a = s < 0 ? -s : s
    if (a > peak) peak = a
    sumSquares += s * s
  }

  // An empty buffer is silent, not an error: a zero-length clip is a real
  // thing to be handed, and dividing by its length is not.
  const silent = peak === 0 || samples.length === 0
  const rms = samples.length ? Math.sqrt(sumSquares / samples.length) : 0
  const db = (v: number) => (v > 0 ? 20 * Math.log10(v) : SILENT_DBFS)

  return {
    peakDbfs: silent ? SILENT_DBFS : db(peak),
    rmsDbfs: silent ? SILENT_DBFS : db(rms),
    // >= 1.0, not > 1.0: full scale is already the ceiling, and a sample
    // sitting exactly on it is the signature of something upstream having
    // clipped rather than a coincidence.
    clipped: peak >= 1,
    silent,
  }
}

/* Where a signal should sit to be listened to.
 *
 * Peak-led, and that is the whole rule. The sounds worth hearing on a
 * hydrophone are transients, and 15-20 dB of peak above RMS is ordinary here,
 * so a gain chosen to hit an RMS target puts the peaks at or through full
 * scale. `live.ts` learned that from a recording that came back peaking at
 * 0.7 dBFS -- clipped -- under a rule that aimed RMS at -20 dBFS and ignored
 * crest factor entirely.
 *
 * Twelve dB of headroom below full scale: enough that a transient half as loud
 * again as anything in the measurement window still fits. Quiet is recoverable
 * where clipped is not.
 */
export const LISTEN_TARGET_PEAK_DBFS = -12

/* The secondary bound. It stops a single click in an otherwise silent window
 * from deciding that everything around it should be inaudible. */
export const LISTEN_TARGET_RMS_DBFS = -26

/* A ceiling on the boost, because the quietest thing on the card is not signal.
 * A recording 110 dB down is the converter's own noise floor, and lifting that
 * to a listening level produces confident-sounding hiss. Stopping at 60 dB
 * means such a recording stays quiet, which is the truthful outcome. */
export const LISTEN_MAX_BOOST_DB = 60

/** How much to lift something for listening, in dB.
 *
 *  One rule for every player in this app: the live monitor, a single archived
 *  clip, and a multi-segment span. It was three rules with two sets of targets,
 *  and the archive aimed its peaks at -1 dBFS where live aims at -12 -- so the
 *  same hydrophone played about 10 dB louder from storage than it did live, and
 *  the archive had no limiter to catch the difference. Nothing about a
 *  recording changes between hearing it now and hearing it tomorrow, so nothing
 *  about the gain should either.
 *
 *  **Never written to a file.** This is a property of one act of listening, not
 *  of the audio: it belongs on a GainNode on the way to the speaker and nowhere
 *  else. What gets baked into a saved clip is a different question, asked and
 *  answered separately in `live.ts` -- the two agree today, and are kept apart
 *  so that tuning what you hear cannot quietly change what is stored.
 *
 *  Returns 0 for silence, where no amount of gain produces anything and a
 *  computed one would be Infinity.
 *
 *  `neverAttenuate` floors the result at unity, for a live monitor that gets
 *  one pass at a signal and has a limiter behind it. A file can be turned down
 *  as well as up, because it can be played again.
 */
export function listeningGainDb(
  l: Pick<Levels, 'peakDbfs' | 'rmsDbfs' | 'silent'> | null,
  opts: { neverAttenuate?: boolean } = {},
): number {
  if (!l || l.silent) return 0
  const db = Math.min(
    LISTEN_TARGET_PEAK_DBFS - l.peakDbfs,
    LISTEN_TARGET_RMS_DBFS - l.rmsDbfs,
    LISTEN_MAX_BOOST_DB,
  )
  return opts.neverAttenuate ? Math.max(0, db) : db
}

export interface Spectrogram {
  /** One column per time step, each `fftSize/2` magnitudes in dBFS. */
  columns: Float32Array[]
  fftSize: number
  hop: number
  sampleRate: number
  duration: number
}

/** Short-time Fourier transform of one channel.
 *
 *  `maxColumns` bounds the work by the width of the canvas rather than the
 *  length of the file: an hour of audio at hop 512 would be 340 000 columns,
 *  of which a 1200-pixel canvas can show 1200. The hop is widened instead, so
 *  long files stay interactive and no column is computed to be thrown away.
 */
export function computeSpectrogram(
  samples: Float32Array,
  sampleRate: number,
  fftSize = 2048,
  maxColumns = 1600,
): Spectrogram {
  const bins = fftSize >> 1
  const minHop = fftSize >> 2
  const wanted = Math.max(1, Math.ceil((samples.length - fftSize) / maxColumns))
  const hop = Math.max(minHop, wanted)

  const win = hann(fftSize)
  // Coherent gain of the window, so a full-scale sine reads ~0 dBFS instead of
  // an arbitrary offset that would silently change with fftSize.
  let winSum = 0
  for (let i = 0; i < fftSize; i++) winSum += win[i]!
  const scale = 2 / winSum

  const re = new Float32Array(fftSize)
  const im = new Float32Array(fftSize)
  const columns: Float32Array[] = []

  for (let off = 0; off + fftSize <= samples.length; off += hop) {
    for (let i = 0; i < fftSize; i++) {
      re[i] = samples[off + i]! * win[i]!
      im[i] = 0
    }
    fft(re, im)

    const col = new Float32Array(bins)
    for (let k = 0; k < bins; k++) {
      const mag = Math.hypot(re[k]!, im[k]!) * scale
      // -200 dB floor rather than -Infinity: a true zero bin is common in
      // digital silence and would poison every later arithmetic operation.
      col[k] = mag > 0 ? 20 * Math.log10(mag) : -200
    }
    columns.push(col)
  }

  return {
    columns, fftSize, hop, sampleRate,
    duration: samples.length / sampleRate,
  }
}

/* --------------------------------------------------------------- colormap */

/* The brand palette, used as a scale rather than as decoration.
 *
 * Deep -> Signal -> Pulse maps exactly onto quiet -> present -> loud, and the
 * 85/12/3 ratio the guide insists on falls out of the physics for free: most
 * of a hydrophone spectrogram is background, so most of it is Deep, and red
 * appears only in the top of the range where energy is genuinely unusual.
 * "Used sparingly, only ever as the moment of detection."
 */
/* One hue, ramping in lightness, and no red.
 *
 * The previous ramp climbed steadily across the whole range and ended at the
 * Pulse red, which had two effects. Everything looked equally important,
 * because every part of the range was given the same amount of visual weight.
 * And loud read as alarming, because the top of the scale was the colour this
 * design reserves for a detection: the brand guide puts red at 3% of a frame
 * and calls the restraint the brand, so spending it on "this bin is loud" left
 * nothing to say "this is the thing you are looking for".
 *
 * Now the bottom half stays close to background. Quiet looks quiet, structure
 * emerges from the upper third, and red is free again for marks that mean
 * something.
 */
const STOPS: [number, [number, number, number]][] = [
  [0.0, [4, 21, 15]],
  [0.42, [7, 33, 25]],
  [0.62, [19, 66, 49]],
  [0.80, [63, 126, 99]],
  [0.92, [138, 190, 165]],
  [1.0, [214, 236, 225]],
]

/** 256-entry lookup, built once. Per-pixel interpolation across six stops in
 *  a render loop that runs 60 times a second is the difference between a
 *  smooth waterfall and a stuttering one. */
export const LUT: Uint8ClampedArray = (() => {
  const lut = new Uint8ClampedArray(256 * 3)
  for (let i = 0; i < 256; i++) {
    const t = i / 255
    let a = STOPS[0]!
    let b = STOPS[STOPS.length - 1]!
    for (let s = 0; s < STOPS.length - 1; s++) {
      if (t >= STOPS[s]![0] && t <= STOPS[s + 1]![0]) {
        a = STOPS[s]!
        b = STOPS[s + 1]!
        break
      }
    }
    const span = b[0] - a[0] || 1
    const f = (t - a[0]) / span
    lut[i * 3] = a[1][0] + (b[1][0] - a[1][0]) * f
    lut[i * 3 + 1] = a[1][1] + (b[1][1] - a[1][1]) * f
    lut[i * 3 + 2] = a[1][2] + (b[1][2] - a[1][2]) * f
  }
  return lut
})()

/** The dB window this particular recording actually occupies.
 *
 *  The static view used a fixed -95..-20 dBFS pair, and a clip captured at
 *  +50 dB of gain sits nowhere near it: everything above -20 clamps to the top
 *  of the ramp, so a third of the picture came out solid red. That is not a
 *  loud recording rendered honestly, it is a scale mismatch rendered
 *  confidently -- and red is the one colour in this design that is supposed to
 *  mean something, at 3% of the frame rather than 30%.
 *
 *  Percentiles, not min/max: one dead bin must not set the floor and one
 *  clipped sample must not push everything else into the dark. Sampled rather
 *  than exhaustive, because a 40 MB clip is millions of values and the answer
 *  from a few thousand of them is the same to within a fraction of a dB.
 */
export function autoRange(spec: Spectrogram): [number, number] {
  const vals: number[] = []
  const cStep = Math.max(1, Math.floor(spec.columns.length / 160))
  for (let c = 0; c < spec.columns.length; c += cStep) {
    const col = spec.columns[c]!
    const bStep = Math.max(1, Math.floor(col.length / 240))
    for (let i = 0; i < col.length; i += bStep) {
      const v = col[i]!
      if (Number.isFinite(v)) vals.push(v)
    }
  }
  if (!vals.length) return [-95, -20]
  vals.sort((a, b) => a - b)
  const q = (f: number) => vals[Math.min(vals.length - 1, Math.round(f * (vals.length - 1)))]!
  const lo = q(0.04)
  // 0.999 rather than the maximum, so a single clipped bin does not compress
  // everything real into the bottom of the ramp.
  let hi = q(0.999)
  // A floor on the span. A near-silent recording otherwise gets its own noise
  // floor stretched across the whole colour ramp and rendered as weather.
  if (hi - lo < 32) hi = lo + 32
  return [Math.round(lo), Math.round(hi)]
}

/* ------------------------------------------------------------ axis mapping */

/** Lowest frequency drawn. Below this is mostly platform noise and mooring
 *  rumble, and giving it a third of the axis would waste the part of the
 *  picture where the biology and the vessels are. */
export const F_MIN = 20

/** Row -> bin index, logarithmic in frequency.
 *
 *  Log, not linear, because underwater sound is read that way: octaves and
 *  third-octaves. On a linear axis at 48 kHz everything below 2 kHz -- which is
 *  most vessel noise and most fish -- is squeezed into the bottom 4% of the
 *  image.
 */
export function rowToBin(row: number, height: number, bins: number, sampleRate: number): number {
  const nyq = sampleRate / 2
  const f = F_MIN * Math.pow(nyq / F_MIN, 1 - row / (height - 1))
  return Math.min(bins - 1, Math.max(0, Math.round((f / nyq) * (bins - 1))))
}

/** Tick positions for the frequency axis, as fractions from the top. */
export function axisTicks(sampleRate: number): { label: string; frac: number }[] {
  const nyq = sampleRate / 2
  const candidates = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000]
  const span = Math.log(nyq / F_MIN)
  return candidates
    .filter((f) => f >= F_MIN && f <= nyq)
    .map((f) => ({
      label: f >= 1000 ? `${f / 1000}k` : `${f}`,
      frac: 1 - Math.log(f / F_MIN) / span,
    }))
}

/** Paint one column of dB values into an RGBA buffer, log-mapped. */
export function paintColumn(
  px: Uint8ClampedArray,
  width: number,
  height: number,
  x: number,
  db: Float32Array,
  sampleRate: number,
  minDb: number,
  maxDb: number,
): void {
  const bins = db.length
  const span = maxDb - minDb || 1
  const nyq = sampleRate / 2
  const logSpan = Math.log(nyq / F_MIN)

  for (let y = 0; y < height; y++) {
    /* Every bin the row covers, not one sampled from the middle of them.
     *
     * The axis is logarithmic, so at the top of the picture a single row spans
     * dozens of FFT bins while at the bottom many rows share one. Point
     * sampling that is aliasing, and aliasing a noisy spectrum is exactly the
     * dense speckle that made the high frequencies look like static: the
     * picture was showing which arbitrary bin the row happened to land on
     * rather than what was in the row.
     *
     * Averaged in linear power and converted back, because a mean of decibels
     * is a mean of logarithms and is not the energy in the row.
     */
    const fTop = F_MIN * Math.exp(logSpan * (1 - (y - 0.5) / (height - 1)))
    const fBot = F_MIN * Math.exp(logSpan * (1 - (y + 0.5) / (height - 1)))
    let b0 = Math.round((fBot / nyq) * (bins - 1))
    let b1 = Math.round((fTop / nyq) * (bins - 1))
    b0 = b0 < 0 ? 0 : b0 > bins - 1 ? bins - 1 : b0
    b1 = b1 < b0 ? b0 : b1 > bins - 1 ? bins - 1 : b1

    let acc = 0
    let n = 0
    for (let b = b0; b <= b1; b++) {
      const v = db[b]!
      if (Number.isFinite(v)) { acc += Math.pow(10, v / 10); n++ }
    }
    const value = n ? 10 * Math.log10(acc / n) : minDb

    let t = (value - minDb) / span
    t = t < 0 ? 0 : t > 1 ? 1 : t
    const c = (t * 255) | 0
    const o = (y * width + x) * 4
    px[o] = LUT[c * 3]!
    px[o + 1] = LUT[c * 3 + 1]!
    px[o + 2] = LUT[c * 3 + 2]!
    px[o + 3] = 255
  }
}
